/**
 * Shared Utilities for Drag & Drop and File Processing
 * Medical Radar Web App
 */

/**
 * Categorize a list of files into images, videos, PDFs, and others.
 * @param {FileList | File[] | null | undefined} files
 * @returns {{
 *   imageFiles: File[],
 *   videoFiles: File[],
 *   pdfFiles: File[],
 *   otherFiles: File[],
 *   hasFiles: boolean,
 *   firstFile: File | null
 * }}
 */
export const categorizeFiles = (files) => {
  if (!files || files.length === 0) {
    return {
      imageFiles: [],
      videoFiles: [],
      pdfFiles: [],
      otherFiles: [],
      hasFiles: false,
      firstFile: null,
    };
  }

  const list = Array.from(files);
  const imageFiles = list.filter(
    (f) => f?.type?.startsWith("image/") || /\.(jpe?g|png|webp|gif|bmp|svg)$/i.test(f?.name || "")
  );
  const videoFiles = list.filter(
    (f) => f?.type?.startsWith("video/") || /\.(mp4|mov|avi|mkv|webm)$/i.test(f?.name || "")
  );
  const pdfFiles = list.filter(
    (f) => f?.type === "application/pdf" || /\.pdf$/i.test(f?.name || "")
  );
  const otherFiles = list.filter(
    (f) => !imageFiles.includes(f) && !videoFiles.includes(f) && !pdfFiles.includes(f)
  );

  return {
    imageFiles,
    videoFiles,
    pdfFiles,
    otherFiles,
    hasFiles: list.length > 0,
    firstFile: list[0] || null,
  };
};

/**
 * Dispatch files directly to a hidden <input type="file" /> element
 * using the DataTransfer API so existing onChange handlers trigger properly.
 * @param {HTMLInputElement | null} inputEl
 * @param {File[]} files
 * @returns {boolean} true if dispatched successfully, false otherwise
 */
export const dispatchFilesToInput = (inputEl, files) => {
  if (!inputEl || !files || files.length === 0) return false;
  if (typeof DataTransfer === "undefined") return false;

  try {
    const dt = new DataTransfer();
    files.forEach((f) => dt.items.add(f));
    inputEl.files = dt.files;
    inputEl.dispatchEvent(new Event("change", { bubbles: true }));
    return true;
  } catch (err) {
    console.warn("[fileDropUtils] DataTransfer dispatch failed:", err);
    return false;
  }
};

/**
 * Read multiple File objects as base64 Data URLs.
 * @param {File[]} files
 * @returns {Promise<string[]>}
 */
export const readFilesAsDataUrls = (files) => {
  if (!files || files.length === 0) return Promise.resolve([]);
  return Promise.all(
    Array.from(files).map((f) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(f);
      });
    })
  );
};

/**
 * Read a single File as base64 Data URL.
 * @param {File} file
 * @returns {Promise<string>}
 */
export const readFileAsDataUrl = (file) => {
  if (!file) return Promise.reject(new Error("No file provided"));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};

/**
 * Create reusable dropzone event handlers for React components.
 * @param {Function} onFilesDropped - Callback when files are dropped: (files: File[], event: DragEvent) => void
 * @param {Function} [setIsDragging] - Optional state setter for active drag state: (isDragging: boolean) => void
 * @returns {{
 *   onDragOver: (e: React.DragEvent) => void,
 *   onDragEnter: (e: React.DragEvent) => void,
 *   onDragLeave: (e: React.DragEvent) => void,
 *   onDrop: (e: React.DragEvent) => void
 * }}
 */
export const createDropzoneHandlers = (onFilesDropped, setIsDragging) => {
  return {
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (setIsDragging) setIsDragging(true);
    },
    onDragEnter: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (setIsDragging) setIsDragging(true);
    },
    onDragLeave: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!e.currentTarget.contains(e.relatedTarget)) {
        if (setIsDragging) setIsDragging(false);
      }
    },
    onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (setIsDragging) setIsDragging(false);

      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length > 0 && typeof onFilesDropped === "function") {
        onFilesDropped(files, e);
      }
    },
  };
};
